import { ReactNode, useEffect, useMemo, useState } from 'react'
import {
  initialQueryState,
  OptionsHelper,
  QueryFilter,
  showError,
  useDebounce,
} from '../../../../../../../_metronic/helpers'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'
import { getLimiteUnidades } from '../../core/_requests'
import { UnidadeLimiteStatus } from '../../core/_models'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  Input,
  IOption,
  TableFilter,
  TableFilters,
} from '../../../../../../components'
import { useT } from '_metronic/i18n/Metronici18n'

type Props = {
  /** Conteúdo à direita da linha dos campos (ex.: "Adicionar" ou barra de seleção em lote). */
  actions?: ReactNode
}

export const UnidadesListSearchComponent = ({ actions }: Props) => {
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { t } = useT()
  const [limiteStatus, setLimiteStatus] = useState<UnidadeLimiteStatus | null>(null)
  const total = response?.totalElements ?? 0

  const debouncedFilters = useDebounce(filters, 500)

  const search = useMemo(
    () => (debouncedFilters.unidade ? `nome~*${debouncedFilters.unidade}*` : ''),
    [debouncedFilters]
  )

  // A instituição vai em `filters` (idEmpresa), não no `search`.
  const filtersEmpresa = useMemo(
    (): QueryFilter[] | undefined =>
      debouncedFilters.empresa
        ? [{ field: 'idEmpresa', operator: '=', value: Number(debouncedFilters.empresa.value) }]
        : undefined,
    [debouncedFilters]
  )

  useEffect(() => {
    updateState({
      ...initialQueryState,
      search,
      filters: filtersEmpresa,
    })
  }, [search, filtersEmpresa, updateState])

  // Contador "unidades ativas / limite" da instituição selecionada.
  useEffect(() => {
    const empresaId = debouncedFilters.empresa?.value
    if (!empresaId) {
      setLimiteStatus(null)
      return
    }
    getLimiteUnidades(Number(empresaId)).then(setLimiteStatus).catch(showError)
  }, [debouncedFilters.empresa, response])

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo.
  const activeFilters: ActiveFilter[] = [
    filters.unidade && {
      key: 'unidade',
      label: t('cols.unidade'),
      value: `“${filters.unidade}”`,
      onRemove: () => setFilter('unidade', ''),
    },
    filters.empresa && {
      key: 'empresa',
      label: t('empresa.label'),
      value: filters.empresa.label,
      onRemove: () => setFilter('empresa', undefined),
    },
  ].filter((filter): filter is ActiveFilter => Boolean(filter))

  const resultado = isLoading
    ? 'Buscando…'
    : `${total} ${total === 1 ? 'unidade encontrada' : 'unidades encontradas'}`
  const limite = limiteStatus
    ? ` · Ativas: ${limiteStatus.unidades} / ${limiteStatus.limite ?? '∞'}`
    : ''

  return (
    <TableFilters
      actions={actions}
      footer={
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={clearFilters}
          summary={`${resultado}${limite}`}
        />
      }
    >
      <TableFilter label={t('cols.unidade')} wide>
        <Input
          placeholder='Digite a unidade'
          value={filters.unidade}
          onChange={(e) => setFilter('unidade', e.target.value)}
        />
      </TableFilter>
      <TableFilter label={t('empresa.label')}>
        <CustomSelect
          entity='empresa'
          onChange={(empresa?: IOption<number>) => setFilter('empresa', empresa ?? undefined)}
          fetchOptions={OptionsHelper.getOptionsEmpresas}
          value={filters.empresa}
        />
      </TableFilter>
    </TableFilters>
  )
}
